import * as React from "react";
import { useDataProvider } from "react-admin";
import { Grid, Typography } from "@material-ui/core";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import useAiInsight from "../../../Components/AiAssistant/useAiInsight";
import { buildAiBaseParams } from "../../../Components/AiAssistant/buildAiBaseParams";

const gridContainerProps: any = {
  container: true,
  direction: "row",
  item: true,
  xs: 12,
  spacing: 1,
  justify: "space-around",
  alignItems: "center",
};

const MonthlyServiceableServiceLevelComponent = ({ filterParams }) => {
  const dataProvider = useDataProvider();
  const [data, setData] = React.useState([] as any);
  const resourceEndpoint = "/pbhrequestserviceables/cockpit/monthlyserviceableservicelevel";

  const insightParams = React.useMemo(() => {
    if (!data.length) return null;
    const totalOntime = data.reduce((s: number, item: any) => s + (item.ontimeCount ?? 0), 0);
    const totalDelayed = data.reduce((s: number, item: any) => s + (item.delayedCount ?? 0), 0);
    const grandTotal = totalOntime + totalDelayed;
    const serviceLevelRate = grandTotal > 0 ? ((totalOntime / grandTotal) * 100).toFixed(1) + "%" : "0%";
    const monthlyTrend = data
      .map((item: any) => {
        const tot = (item.ontimeCount ?? 0) + (item.delayedCount ?? 0);
        const rate = tot > 0 ? ((item.ontimeCount / tot) * 100).toFixed(1) : "0.0";
        return `${item.month}:${rate}%`;
      })
      .join(",");
    return {
      ...buildAiBaseParams(filterParams),
      totalOntime: totalOntime.toString(),
      totalDelayed: totalDelayed.toString(),
      periodCount: data.length.toString(),
      serviceLevelRate,
      monthlyTrend,
    };
  }, [filterParams, data]);
  useAiInsight("monthlyserviceableservicelevelcomponent", insightParams);

  React.useEffect(() => {
    GetMonthlyServiceableServiceLevelData();
  }, [filterParams]);

  const GetMonthlyServiceableServiceLevelData = async () => {
    await dataProvider
      .getList(resourceEndpoint, {
        pagination: { page: 1, perPage: 999999 },
        sort: { field: "id", order: "ASC" },
        filter: filterParams,
      })
      .then(({ data }) => {
        setData(data);
      })
      .catch(error => {
        console.log(error);
      });
  };

  // Verileri yüzde formatına çevir
  const processedData =
    data?.map(item => {
      const total = item.delayedCount + item.ontimeCount;
      return {
        ...item,
        ontimePercentDecimal: total > 0 ? (item.ontimeCount / total) * 100 : 0,
        delayedPercentDecimal: total > 0 ? (item.delayedCount / total) * 100 : 0,
      };
    }) || [];

  return (
    <>
      <Typography gutterBottom variant="h5" component="h2" align="center">
        Monthly Serviceable Service Level and Delayed Trends - ALL
      </Typography>
      <Grid {...gridContainerProps} alignItems={"flex-start"}>
        <Grid item xs={12} sm={5}>
          <div style={{ width: 1000, height: 400 }}>
            <ResponsiveContainer>
              <ComposedChart
                width={500}
                height={400}
                data={processedData}
                margin={{
                  top: 20,
                  right: 20,
                  bottom: 20,
                  left: 20,
                }}
              >
                <CartesianGrid stroke="#f5f5f5" />
                <XAxis offset={0} dataKey="month" scale="point" padding={{ left: 20, right: 20 }} />
                <YAxis yAxisId="left" orientation="left" tickFormatter={value => value} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={value => `${value}%`} />
                <Tooltip
                  formatter={(value, name) => [
                    name === "ontimePercentDecimal" ? `${Number(value).toFixed(2)}%` : value,
                    name,
                  ]}
                  labelFormatter={label => label}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div
                          style={{
                            backgroundColor: "white",
                            padding: "10px",
                            border: "1px solid #ccc",
                            borderRadius: "5px",
                          }}
                        >
                          <p style={{ margin: 0 }}>{`Month: ${label}`}</p>
                          <p style={{ margin: 0, color: "#45B4C8" }}>{`Delayed: ${data.delayedCount || 0}`}</p>
                          <p
                            style={{ margin: 0, color: "#8DCB2A" }}
                          >{`Service Level: ${Number(data.ontimePercentDecimal || 0).toFixed(2)}%`}</p>
                          <p
                            style={{ margin: 0, color: "#FF6B6B" }}
                          >{`Delayed Percent: ${Number(data.delayedPercentDecimal || 0).toFixed(2)}%`}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend
                  payload={[
                    { value: "Delayed", type: "line", id: "delayedCount", color: "#45B4C8" },
                    { value: "Service Level", type: "line", id: "ontimePercentDecimal", color: "#8DCB2A" },
                  ]}
                />
                <Bar yAxisId="left" label="Delayed" dataKey="delayedCount" barSize={20} fill="#45B4C8" />
                <Line
                  yAxisId="right"
                  label="Service Level"
                  type="monotone"
                  dataKey="ontimePercentDecimal"
                  stroke="#8DCB2A"
                  fill="#8DCB2A"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Grid>
      </Grid>
    </>
  );
};

export default MonthlyServiceableServiceLevelComponent;
